import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Settings, MapPin, AlertCircle, Shield, AlertTriangle } from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";
import AutoPunchOutTab from "@/components/attendance/AutoPunchOutTab";
import GeneralSettingsTab from "@/components/attendance/GeneralSettingsTab";
import AccessControlTab from "@/components/attendance/AccessControlTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AttendanceSettings = () => {
  const { selectedBusinessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [activeTab, setActiveTab] = useState("general");
  const [settings, setSettings] = useState({
    office_start_time: "10:00",
    office_end_time: "18:00",
    working_hours_per_day: 8,
    half_day_hours: 4,
    late_grace_minutes: 15,
    late_penalty_amount: 0,
    overtime_rate_multiplier: 1.5,
    allow_early_punch_in_minutes: 30,
    require_selfie: true,
    require_gps: true,
    timezone: "Asia/Kolkata",
    weekly_offs: ["sunday"],
    exclude_holidays: true,
    late_deduction_type: "fixed",
    late_marks_to_half_day: 3,
    full_day_min_hours: 9,
    half_day_min_hours: 4.5,
    salary_calculation_days: 30
  });
  
  const [locations, setLocations] = useState([]);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: "",
    address: "",
    latitude: "",
    longitude: "",
    radius_meters: 100
  });
  
  const [accessControl, setAccessControl] = useState({ admin: [], manager: [], staff: [] });
  const [availablePages, setAvailablePages] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [savingAccess, setSavingAccess] = useState(false);
  const [missingPunchOutCount, setMissingPunchOutCount] = useState(0);

  const fetchUserRole = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/settings?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/settings`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedBusinessId]);

  const fetchLocations = useCallback(async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/locations?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/locations`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  }, [selectedBusinessId]);

  const fetchMissingPunchOutCount = useCallback(async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/missing-punch-outs?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/missing-punch-outs`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setMissingPunchOutCount(data.records?.length || 0);
      }
    } catch (error) {
      console.error("Error fetching missing punch-out count:", error);
    }
  }, [selectedBusinessId]);

  const fetchAccessControl = useCallback(async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/access-control?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/access-control`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setAccessControl(data.settings || {});
        setAvailablePages(data.available_pages || []);
      }
    } catch (error) {
      console.error("Error fetching access control:", error);
    }
  }, [selectedBusinessId]);

  const fetchAccessLogs = useCallback(async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/access-control/logs?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/access-control/logs`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setAccessLogs(data || []);
      }
    } catch (error) {
      console.error("Error fetching access logs:", error);
    }
  }, [selectedBusinessId]);

  useEffect(() => {
    fetchUserRole();
    fetchSettings();
    fetchLocations();
  }, [fetchUserRole, fetchSettings, fetchLocations]);

  useEffect(() => {
    if (userRole === "super_admin") {
      fetchAccessControl();
      fetchAccessLogs();
    }
    if (["super_admin", "admin"].includes(userRole)) {
      fetchMissingPunchOutCount();
    }
  }, [userRole, fetchAccessControl, fetchAccessLogs, fetchMissingPunchOutCount]);

  const canManage = ["super_admin", "admin"].includes(userRole);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      let url = `${BACKEND_URL}/api/attendance/settings`;
      if (selectedBusinessId) {
        url += `?business_id=${selectedBusinessId}`;
      }
      
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success("Settings saved successfully");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAddLocation = async () => {
    if (!newLocation.name || !newLocation.latitude || !newLocation.longitude) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      let url = `${BACKEND_URL}/api/attendance/locations`;
      if (selectedBusinessId) {
        url += `?business_id=${selectedBusinessId}`;
      }
      
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newLocation,
          latitude: parseFloat(newLocation.latitude),
          longitude: parseFloat(newLocation.longitude)
        })
      });

      if (response.ok) {
        toast.success("Location added successfully");
        setShowAddLocation(false);
        setNewLocation({ name: "", address: "", latitude: "", longitude: "", radius_meters: 100 });
        fetchLocations();
      } else {
        toast.error("Failed to add location");
      }
    } catch (error) {
      toast.error("Error adding location");
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!confirm("Are you sure you want to delete this location?")) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/locations/${locationId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Location deleted");
        fetchLocations();
      } else {
        toast.error("Failed to delete location");
      }
    } catch (error) {
      toast.error("Error deleting location");
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setNewLocation({
          ...newLocation,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        });
        toast.success("Location captured");
      },
      () => toast.error("Failed to get location")
    );
  };

  const handleToggleAccess = (role, pageId) => {
    setAccessControl(prev => {
      const currentPages = prev[role] || [];
      const hasAccess = currentPages.includes(pageId);
      return {
        ...prev,
        [role]: hasAccess ? currentPages.filter(p => p !== pageId) : [...currentPages, pageId]
      };
    });
  };

  const handleSaveAccessControl = async (role) => {
    setSavingAccess(true);
    try {
      let url = `${BACKEND_URL}/api/attendance/access-control`;
      if (selectedBusinessId) {
        url += `?business_id=${selectedBusinessId}`;
      }
      
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role, pages: accessControl[role] || [] })
      });

      if (response.ok) {
        toast.success(`Access control updated for ${role}`);
        fetchAccessLogs();
      } else {
        toast.error("Failed to update access control");
      }
    } catch (error) {
      toast.error("Error updating access control");
    } finally {
      setSavingAccess(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const getPageLabel = (pageId) => {
    const page = availablePages.find(p => p.id === pageId);
    return page?.label || pageId;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="attendance-settings-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Settings className="w-8 h-8 text-emerald-600" />
            Attendance Settings
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Configure attendance, payroll rules, and access control
          </p>
        </div>
      </div>

      {!canManage && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <span className="text-sm text-amber-700 dark:text-amber-400">
              Only administrators can modify these settings
            </span>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:w-auto md:inline-flex md:grid-cols-3">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">General Settings</span>
            <span className="sm:hidden">General</span>
          </TabsTrigger>
          {canManage && (
            <TabsTrigger value="auto-punchout" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">Auto Punch-Out</span>
              <span className="sm:hidden">Auto P/O</span>
              {missingPunchOutCount > 0 && (
                <span className="ml-1 h-5 px-1.5 text-xs bg-red-500 text-white rounded-full flex items-center justify-center">
                  {missingPunchOutCount}
                </span>
              )}
            </TabsTrigger>
          )}
          {userRole === "super_admin" && (
            <TabsTrigger value="access" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Access Control</span>
              <span className="sm:hidden">Access</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="general" className="space-y-6 mt-6">
          <GeneralSettingsTab 
            settings={settings}
            setSettings={setSettings}
            canManage={canManage}
            saving={saving}
            handleSaveSettings={handleSaveSettings}
            locations={locations}
            setShowAddLocation={setShowAddLocation}
            handleDeleteLocation={handleDeleteLocation}
          />
        </TabsContent>

        {canManage && (
          <TabsContent value="auto-punchout">
            <AutoPunchOutTab 
              selectedBusinessId={selectedBusinessId}
              userRole={userRole}
              officeEndTime={settings.office_end_time}
            />
          </TabsContent>
        )}

        {userRole === "super_admin" && (
          <TabsContent value="access" className="space-y-6 mt-6">
            <AccessControlTab
              accessControl={accessControl}
              availablePages={availablePages}
              accessLogs={accessLogs}
              savingAccess={savingAccess}
              handleToggleAccess={handleToggleAccess}
              handleSaveAccessControl={handleSaveAccessControl}
              formatDate={formatDate}
              getPageLabel={getPageLabel}
            />
          </TabsContent>
        )}
      </Tabs>

      <AddLocationDialog
        open={showAddLocation}
        onOpenChange={setShowAddLocation}
        newLocation={newLocation}
        setNewLocation={setNewLocation}
        handleAddLocation={handleAddLocation}
        getCurrentLocation={getCurrentLocation}
      />
    </div>
  );
};

const AddLocationDialog = ({ open, onOpenChange, newLocation, setNewLocation, handleAddLocation, getCurrentLocation }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-emerald-600" />
          Add Office Location
        </DialogTitle>
        <DialogDescription>
          Add a new location for geo-fence validation
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="loc_name">Location Name *</Label>
          <Input
            id="loc_name"
            value={newLocation.name}
            onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
            placeholder="e.g., Main Office"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="loc_address">Address</Label>
          <Input
            id="loc_address"
            value={newLocation.address}
            onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
            placeholder="Full address"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="loc_lat">Latitude *</Label>
            <Input
              id="loc_lat"
              type="number"
              step="any"
              value={newLocation.latitude}
              onChange={(e) => setNewLocation({ ...newLocation, latitude: e.target.value })}
              placeholder="28.6139"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="loc_lng">Longitude *</Label>
            <Input
              id="loc_lng"
              type="number"
              step="any"
              value={newLocation.longitude}
              onChange={(e) => setNewLocation({ ...newLocation, longitude: e.target.value })}
              placeholder="77.2090"
            />
          </div>
        </div>
        <Button variant="outline" size="sm" type="button" onClick={getCurrentLocation}>
          <MapPin className="w-4 h-4 mr-2" />
          Use Current Location
        </Button>
        <div className="space-y-2">
          <Label htmlFor="loc_radius">Radius (meters)</Label>
          <Input
            id="loc_radius"
            type="number"
            value={newLocation.radius_meters}
            onChange={(e) => setNewLocation({ ...newLocation, radius_meters: parseInt(e.target.value) })}
          />
          <p className="text-xs text-slate-500">Distance within which punch-in is allowed</p>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={handleAddLocation} className="bg-emerald-600 hover:bg-emerald-700">
          Add Location
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default AttendanceSettings;
